import React, { FC, useEffect, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, Progress, Table, Tag, Spin, Button, Tooltip } from 'antd';
import { UserOutlined, CheckCircleOutlined, BarChartOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Pie, Column } from '@ant-design/plots';
import * as htmlToImage from 'html-to-image';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import download from 'downloadjs';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getSurveyStatistics, downloadSurveyStatistics } from '../../core/survey/surveyApi';
import { SurveyStatistics as SurveyStatisticsType, TextAnswer, QuestionAnswer } from '../../core/survey/types';
import './style.scss';

const { Title, Text } = Typography;

const SurveyStatistics: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { singleSurvey, surveyStatistics, isFetching } = useSelector((state: CombinedStates) => state.survey);
    const pieChartRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const barChartRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    useEffect(() => {
        if (singleSurvey?.id && !surveyStatistics) {
            dispatch(getSurveyStatistics({ surveyId: singleSurvey.id }));
        }
    }, [singleSurvey?.id, surveyStatistics, dispatch]);

    const handleRefresh = () => {
        if (singleSurvey?.id) {
            dispatch(getSurveyStatistics({ surveyId: singleSurvey.id }));
        }
    };

    const handleDownloadStatistics = async () => {
        if (!singleSurvey?.id) {
            return;
        }

        try {
            const response = await dispatch(downloadSurveyStatistics({ surveyId: singleSurvey.id })).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `apklausos_statistika_${singleSurvey.id}_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const handleDownloadPieChart = async (questionId: number, questionTitle: string) => {
        const container = pieChartRefs.current[questionId];
        if (container) {
            try {
                const dataUrl = await htmlToImage.toPng(container, {
                    quality: 1,
                    pixelRatio: 2,
                    backgroundColor: '#ffffff',
                });
                const sanitizedTitle = questionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                download(dataUrl, `atsakymu-pasiskirstymas-klausimas-${questionId}-${sanitizedTitle}-${new Date().toISOString().split('T')[0]}.png`);
            } catch (error) {
                console.error('Error downloading pie chart:', error);
            }
        }
    };

    const handleDownloadBarChart = async (questionId: number, questionTitle: string) => {
        const container = barChartRefs.current[questionId];
        if (container) {
            try {
                const dataUrl = await htmlToImage.toPng(container, {
                    quality: 1,
                    pixelRatio: 2,
                    backgroundColor: '#ffffff',
                });
                const sanitizedTitle = questionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                download(dataUrl, `stulpeliu-diagrama-klausimas-${questionId}-${sanitizedTitle}-${new Date().toISOString().split('T')[0]}.png`);
            } catch (error) {
                console.error('Error downloading bar chart:', error);
            }
        }
    };

    const getQuestionTypeLabel = (type: string) => {
        switch (type) {
        case 'text':
            return { color: 'blue', label: 'Tekstinis atsakymas' };
        case 'single':
            return { color: 'green', label: 'Vienas pasirinkimas' };
        case 'multiple':
            return { color: 'purple', label: 'Keli pasirinkimai' };
        case 'paragraph':
            return { color: 'orange', label: 'Paragrafas' };
        default:
            return { color: 'default', label: type };
        }
    };

    const renderQuestionStatistics = (question: SurveyStatisticsType['questions_statistics'][0]) => {
        const typeInfo = getQuestionTypeLabel(question.question_type);

        return (
            <Card
                key={question.question_id}
                title={(
                    <div>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                            {question.question_title}
                        </div>
                        <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
                    </div>
                )}
                style={{ marginBottom: 16 }}>
                {question.question_type !== 'paragraph' && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={8}>
                            <Statistic
                                title="Atsakymų skaičius"
                                value={question.total_answers}
                                prefix={<CheckCircleOutlined />} />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Atsakymo procentas"
                                value={question.answer_rate_percentage}
                                suffix="%"
                                precision={1} />
                        </Col>
                        <Col span={8}>
                            <div>
                                <Text type="secondary">Atsakymo procentas</Text>
                                <Progress
                                    percent={question.answer_rate_percentage}
                                    size="small"
                                    status="active" />
                            </div>
                        </Col>
                    </Row>
                )}
                {question.question_type === 'text' && (
                    <div>
                        <Title level={5}>Tekstiniai atsakymai</Title>
                        {question.answers && question.answers.length > 0 && (
                            <div>
                                {(question.answers as TextAnswer[]).map((textAnswer: TextAnswer) => (
                                    <Card
                                        key={`text-answer-${question.question_id}-${textAnswer.answer_text.slice(0, 20)}-${textAnswer.submitted_at || 'no-date'}-${textAnswer.ip_address || 'no-ip'}`}
                                        size="small"
                                        style={{ marginBottom: 8 }}
                                        bodyStyle={{ padding: '12px 16px' }}>
                                        <Text>{textAnswer.answer_text}</Text>
                                        {textAnswer.submitted_at && (
                                            <div style={{ marginTop: 4 }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    Pateikta: {new Date(textAnswer.submitted_at).toLocaleString('lt-LT')}
                                                </Text>
                                            </div>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {question.answers && question.answers.length > 0 && question.question_type !== 'paragraph' && question.question_type !== 'text' && (
                    <div>
                        <Title level={5}>Atsakymų pasiskirstymas</Title>
                        <Table
                            dataSource={question.answers as QuestionAnswer[]}
                            columns={[
                                {
                                    title: 'Atsakymo variantas',
                                    dataIndex: 'option_title',
                                    key: 'option_title',
                                },
                                {
                                    title: 'Pasirinkimų skaičius',
                                    dataIndex: 'count',
                                    key: 'count',
                                    render: (count: number) => (
                                        <Statistic value={count} />
                                    ),
                                },
                                {
                                    title: 'Procentas',
                                    dataIndex: 'percentage',
                                    key: 'percentage',
                                    render: (percentage: number) => (
                                        <div>
                                            <Text strong>{percentage}%</Text>
                                            <Progress
                                                percent={percentage}
                                                size="small"
                                                showInfo={false} />
                                        </div>
                                    ),
                                },
                            ]}
                            pagination={false}
                            size="small" />

                        {(() => {
                            const pieData = (question.answers as QuestionAnswer[]).map((answer, index) => ({
                                type: answer.option_title,
                                Rezultatas: answer.count,
                                Procentas: answer.percentage,
                                index,
                            }));

                            if (!pieData || pieData.length === 0) return null;

                            // Surasti visas 0 reikšmes
                            const zeroValues = pieData.filter(d => d.Rezultatas === 0);
                            const zeroTypes = zeroValues.map(d => d.type).join(', ');
                            const firstZeroIndex = zeroValues.length > 0 && zeroValues[0] !== undefined ? zeroValues[0].index : -1;

                            const config = {
                                data: pieData,
                                angleField: 'Rezultatas',
                                colorField: 'type',
                                radius: 0.8,
                                label: {
                                    text: (d: { type: string; Rezultatas: number; Procentas: number; index: number }) => {
                                        // Jei rezultatas yra 0, rodome tik pirmą kartą su visomis 0 reikšmėmis
                                        if (d.Rezultatas === 0) {
                                            // Rodome tik pirmam 0 elementui
                                            if (d.index === firstZeroIndex) {
                                                return `${zeroTypes}: 0`;
                                            }
                                            return '';
                                        }
                                        return `${d.type}: ${d.Rezultatas}`;
                                    },
                                    position: 'outside',
                                    connector: true,
                                },
                                tooltip: {
                                    title: (d: { type: string }) => d.type,
                                    formatter: (d: { Rezultatas: number; Procentas: number }) => [
                                        { name: 'Pasirinkimų skaičius', value: d.Rezultatas },
                                    ],
                                },
                                legend: { position: 'bottom' },
                                interactions: [{ type: 'element-active' }],
                            };

                            const barConfig = {
                                data: pieData,
                                xField: 'type',
                                yField: 'Rezultatas',
                                colorField: 'type',
                                label: {
                                    position: 'top' as const,
                                    formatter: (_datum: unknown, context: { Rezultatas?: number; Procentas?: number; type?: string; index?: number }) => {
                                        // G2 formatter: datum yra tik y reikšmė, visi duomenys yra context objekte
                                        const rezultatas = context.Rezultatas ?? 0;
                                        // Jei rezultatas yra 0, nerodome label'ą
                                        if (rezultatas === 0) {
                                            return '';
                                        }
                                        return `${rezultatas}`;
                                    },
                                    style: {
                                        fill: '#ffffff',
                                    },
                                },
                                interactions: [{ type: 'active-region' }],
                            };

                            return (
                                <>
                                    <div style={{ marginTop: '16px' }}>
                                        <div className="pie-chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <Text strong style={{ fontSize: '14px' }}>
                                                Skritulinė diagrama:
                                            </Text>
                                            <Button
                                                type="default"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={() => handleDownloadPieChart(question.question_id, question.question_title)}>
                                                Atsisiųsti
                                            </Button>
                                        </div>
                                        <div
                                            className="able-to-download-pie-chart"
                                            ref={(el) => {
                                                pieChartRefs.current[question.question_id] = el;
                                            }}
                                            style={{ minHeight: '200px', backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '16px' }}>
                                            <Pie {...config} />
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '16px' }}>
                                        <div className="bar-chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <Text strong style={{ fontSize: '14px' }}>
                                                Stulpelinė diagrama:
                                            </Text>
                                            <Button
                                                type="default"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={() => handleDownloadBarChart(question.question_id, question.question_title)}>
                                                Atsisiųsti
                                            </Button>
                                        </div>
                                        <div
                                            className="able-to-download-bar-chart"
                                            ref={(el) => {
                                                barChartRefs.current[question.question_id] = el;
                                            }}
                                            style={{ minHeight: '200px', backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '16px' }}>
                                            <Column {...barConfig} />
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}
            </Card>
        );
    };

    if (isFetching) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text>Kraunama statistika...</Text>
                    </div>
                </div>
            </Card>
        );
    }

    if (!surveyStatistics) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <BarChartOutlined style={{ fontSize: 48, color: '#ccc' }} />
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary">Statistika neprieinama</Text>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div className="survey-statistics">
            <Card
                title={(
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>Apklausos statistika</Title>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Tooltip title="Parsisiųsti statistiką">
                                <Button
                                    type="default"
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadStatistics}
                                    size="middle">Parsisiųsti statistiką
                                </Button>
                            </Tooltip>
                            <Tooltip title="Atnaujinti statistiką">
                                <Button
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                    loading={isFetching}
                                    size="middle" />
                            </Tooltip>
                        </div>
                    </div>
                )}>

                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}>
                        <Statistic
                            title="Bendras atsakymų skaičius"
                            value={surveyStatistics.total_submissions}
                            prefix={<UserOutlined />} />
                    </Col>
                    <Col span={8}>
                        <Statistic
                            title="Klausimų skaičius"
                            value={surveyStatistics.questions_statistics.length}
                            prefix={<BarChartOutlined />} />
                    </Col>
                    <Col span={8}>
                        <div>
                            <Text type="secondary">Atsakymo procentas</Text>
                            <Progress
                                percent={surveyStatistics.total_submissions > 0 ? 100 : 0}
                                status="active" />
                        </div>
                    </Col>
                </Row>

                <Title level={5}>Klausimų statistika</Title>

                {surveyStatistics.questions_statistics.map(renderQuestionStatistics)}
            </Card>
        </div>
    );
};

export default SurveyStatistics;
